import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { Tenant } from '~/api/tenant';
import { STORAGE_KEYS } from '~/storage-keys';

interface TenantState {
  /** Host para o qual `tenant` foi resolvido; outro host nunca o reaproveita. */
  host: string | null;
  tenant: Tenant | null;
  remember(host: string, tenant: Tenant): void;
  forget(): void;
}

/**
 * Último escritório resolvido para o endereço do app, persistido para abrir na hora (e offline) na
 * próxima vez. É da origem, não do usuário: `clearLocalData` não o apaga. Quem decide o escritório
 * continua sendo `GET /api/tenant/`, que o `TenantGate` repete em segundo plano a cada abertura.
 */
export const useTenantStore = create<TenantState>()(
  persist(
    (set) => ({
      host: null,
      tenant: null,
      remember: (host, tenant) => set(() => ({ host, tenant })),
      forget: () => set(() => ({ host: null, tenant: null })),
    }),
    {
      name: STORAGE_KEYS.tenant,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ host: state.host, tenant: state.tenant }),
    },
  ),
);

export function rememberTenant(host: string, tenant: Tenant): void {
  useTenantStore.getState().remember(host, tenant);
}

/** O escritório lembrado para `host`, ou `undefined` sem nenhum ou quando ele é de outro host. */
export function persistedTenantFor(host: string): Tenant | undefined {
  const { host: rememberedHost, tenant } = useTenantStore.getState();
  return rememberedHost === host && tenant ? tenant : undefined;
}

/** Apaga o escritório lembrado quando ele é de outro host (o app abriu em outro endereço). */
export function forgetTenantOfOtherHost(host: string): void {
  const { host: rememberedHost, forget } = useTenantStore.getState();
  if (rememberedHost !== null && rememberedHost !== host) {
    forget();
  }
}

/** Apaga o escritório lembrado para `host`, quando o backend diz que o endereço não tem escritório. */
export function forgetTenantOf(host: string): void {
  const { host: rememberedHost, forget } = useTenantStore.getState();
  if (rememberedHost === host) {
    forget();
  }
}
